/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useIsAuthenticated } from "@azure/msal-react";
import { SignOutButton } from "./SignOutButton";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";

/**
 * Renders the navbar component with a sign-in or sign-out button depending on whether or not a user is authenticated
 * @param props 
 */
export const PageLayout = (props) => {
    const isAuthenticated = useIsAuthenticated();
    const { instance, inProgress } = useMsal();
    const loginAttemptedRef = useRef(false);
    const [authError, setAuthError] = useState("");

    const startLogin = async (loginType = "redirect") => {
        if (isAuthenticated || inProgress !== "none" || loginAttemptedRef.current) {
            return;
        }

        loginAttemptedRef.current = true;
        setAuthError("");

        try {
            if (loginType === "popup") {
                await instance.loginPopup(loginRequest);
            } else if (loginType === "redirect") {
                await instance.loginRedirect(loginRequest);
            }
        } catch (e) {
            const message = e instanceof Error ? e.message : "Unknown authentication error";
            console.error("Login failed:", e);
            setAuthError(message);
            loginAttemptedRef.current = false;
        }
    };

    useEffect(() => {
        startLogin("redirect");
    }, [instance, inProgress, isAuthenticated]);

    return (
        <>
            <div >
                { isAuthenticated ? <SignOutButton /> : <div/> }
            </div>            
            {!isAuthenticated && (
                <Box
                    sx={{
                        minHeight: "100vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 3,
                    }}
                >
                    <Box sx={{ maxWidth: 520, textAlign: "center" }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            Starting Order Tracker...
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            The app is trying to sign you in with Microsoft.
                        </Typography>
                        {!authError && <CircularProgress />}
                        {authError && (
                            <>
                                <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                                    {authError}
                                </Alert>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        loginAttemptedRef.current = false;
                                        startLogin("redirect");
                                    }}
                                >
                                    Try sign in again
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            )}
            {props.children}
        </>
    );
};
